import { createSlice } from "@reduxjs/toolkit";

/**
 * @file gridStateSlice.js
 * @description Redux slice for managing session-level grid state.
 * This slice stores filters, sort info, selections, and pagination per grid ID.
 * State persists during navigation but resets on page refresh.
 */

const initialState = {
  grids: {},
};

export const gridStateSlice = createSlice({
  name: "gridState",
  initialState,
  reducers: {
    /**
     * Update filters for a specific grid
     * @param {string} gridId - Unique identifier for the grid
     * @param {Array} filters - Array of filter objects from ReactDataGrid
     */
    updateGridFilters: (state, action) => {
      const { gridId, filters } = action.payload;
      if (!state.grids[gridId]) {
        state.grids[gridId] = {};
      }
      // Ensure filters is always an array
      state.grids[gridId].filters = Array.isArray(filters) ? filters : [];
    },

    /**
     * Update sort info for a specific grid
     * @param {string} gridId - Unique identifier for the grid
     * @param {Array} sortInfo - Sort configuration from ReactDataGrid
     */
    updateGridSort: (state, action) => {
      const { gridId, sortInfo } = action.payload;
      if (!state.grids[gridId]) {
        state.grids[gridId] = {};
      }
      // Ensure sortInfo is always an array
      state.grids[gridId].sortInfo = Array.isArray(sortInfo) ? sortInfo : [];
    },

    /**
     * Update selection for a specific grid
     * @param {string} gridId - Unique identifier for the grid
     * @param {Object} selection - Selected row IDs object from ReactDataGrid
     */
    updateGridSelection: (state, action) => {
      const { gridId, selection } = action.payload;
      if (!state.grids[gridId]) {
        state.grids[gridId] = {};
      }
      // Selection is an object (keyed by row id), ensure it's at least an object
      state.grids[gridId].selection = selection && typeof selection === 'object' ? selection : {};
    },

    /**
     * Update pagination for a specific grid
     * @param {string} gridId - Unique identifier for the grid
     * @param {Object} pagination - { skip, limit } from ReactDataGrid
     */
    updateGridPagination: (state, action) => {
      const { gridId, pagination } = action.payload;
      if (!state.grids[gridId]) {
        state.grids[gridId] = {};
      }
      state.grids[gridId].pagination = pagination;
    },

    /**
     * Batch update multiple state properties for a grid
     * @param {string} gridId - Unique identifier for the grid
     * @param {Object} updates - Object containing filters, sortInfo, selection, pagination
     */
    updateGridState: (state, action) => {
      const { gridId, updates } = action.payload;
      if (!state.grids[gridId]) {
        state.grids[gridId] = {};
      }
      Object.assign(state.grids[gridId], updates);
    },

    /**
     * Clear session state for a specific grid
     * @param {string} gridId - Unique identifier for the grid
     */
    clearGridState: (state, action) => {
      const { gridId } = action.payload;
      if (state.grids[gridId]) {
        delete state.grids[gridId];
      }
    },

    /**
     * Update column order for a specific grid
     * @param {string} gridId - Unique identifier for the grid
     * @param {Array<string>} columnOrder - Ordered array of column names
     */
    updateGridColumnOrder: (state, action) => {
      const { gridId, columnOrder } = action.payload;
      if (!state.grids[gridId]) {
        state.grids[gridId] = {};
      }
      state.grids[gridId].columnOrder = Array.isArray(columnOrder) ? columnOrder : [];
    },

    /**
     * Clear all grid session states - called on logout
     */
    clearAllGridStates: (state) => {
      state.grids = {};
    },
  },
});

// Export actions
export const {
  updateGridFilters,
  updateGridSort,
  updateGridSelection,
  updateGridPagination,
  updateGridColumnOrder,
  updateGridState,
  clearGridState,
  clearAllGridStates,
} = gridStateSlice.actions;

// Selectors
export const selectGridState = (gridId) => (state) => {
  return state.gridState?.grids?.[gridId] || null;
};

export const selectGridFilters = (gridId) => (state) => {
  return state.gridState?.grids?.[gridId]?.filters || null;
};

export const selectGridSortInfo = (gridId) => (state) => {
  return state.gridState?.grids?.[gridId]?.sortInfo || null;
};

export const selectGridSelection = (gridId) => (state) => {
  return state.gridState?.grids?.[gridId]?.selection || null;
};

export const selectGridPagination = (gridId) => (state) => {
  return state.gridState?.grids?.[gridId]?.pagination || null;
};

export const selectGridColumnOrder = (gridId) => (state) => {
  return state.gridState?.grids?.[gridId]?.columnOrder || null;
};

export const selectAllGridStates = (state) => {
  return state.gridState?.grids || {};
};

export default gridStateSlice.reducer;
